import { useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  findSpokeNameConflict,
  setKnownSpokeNames,
  spokeNameConflictMessage,
} from './spokeNameConflict';

export const SpokeNameField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  idSchema,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [existingNames, setExistingNames] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const response = await fetchApi.fetch(
          `${baseUrl}/spoke-requests/approved`,
        );
        if (!response.ok) return;

        const body = (await response.json()) as {
          items?: { name?: string }[];
        };
        const names = (body.items ?? [])
          .map(item => item.name)
          .filter((name): name is string => Boolean(name));

        if (!cancelled) {
          setExistingNames(names);
          setKnownSpokeNames(names);
        }
      } catch {
        // Fail open: a transient fetch error must not block a legitimate request.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const conflict = formData
    ? findSpokeNameConflict(formData, existingNames)
    : undefined;
  const conflictMessage = conflict
    ? spokeNameConflictMessage(conflict)
    : undefined;

  const schemaErrors = rawErrors ?? [];
  const hasError = schemaErrors.length > 0 || Boolean(conflictMessage);

  let helperText: string | undefined;
  if (conflictMessage) {
    helperText = conflictMessage;
  } else if (schemaErrors.length > 0) {
    helperText = schemaErrors.join(', ');
  } else {
    helperText =
      (uiSchema?.['ui:help'] as string | undefined) ?? schema?.description;
  }

  return (
    <TextField
      id={idSchema?.$id}
      fullWidth
      margin="normal"
      label={schema?.title ?? 'Spoke Name'}
      required={required}
      error={hasError}
      helperText={helperText}
      value={formData ?? ''}
      onChange={e => onChange(e.target.value || undefined)}
      inputProps={
        schema?.maxLength ? { maxLength: schema.maxLength } : undefined
      }
    />
  );
};
